import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import logoPurple from '@/assets/logo-parts-v2/01-purple.png'
import logoGreen from '@/assets/logo-parts-v2/02-green.png'
import logoOrange from '@/assets/logo-parts-v2/03-orange.png'
import logoOverlap from '@/assets/logo-parts-v2/04-dark-purple-overlap.png'
import letterJ from '@/assets/logo-parts/letter-j.png'
import letterO from '@/assets/logo-parts/letter-o.png'
import letterS from '@/assets/logo-parts/letter-s.png'
import wordCowork from '@/assets/logo-parts/word-cowork.png'

// Premium easing — smooth deceleration with a very subtle overshoot, not a bouncy/cartoon spring.
const EASE = [0.22, 1, 0.36, 1] as const

type Phase = 'symbol' | 'typography' | 'hold' | 'out'

// ── Design canvas ────────────────────────────────────────────────────────────
// Every element's FINAL left/top/width is authored once, in pixels, against this
// fixed canvas, then expressed as a % of it — so the stage scales responsively
// but the reconstructed logo's proportions never change. These numbers were
// calibrated against the complete JosCoWork reference mark (icon + wordmark).
const CANVAS_W = 584
const CANVAS_H = 298

const pct = (px: number, of: number) => `${(px / of) * 100}%`

// left/top/width in px on the CANVAS_W × CANVAS_H canvas — this IS the final,
// permanent position of the wordmark. LOCKED — do not modify.
const LAYOUT = {
  j:       { left: 4.39,   top: 205.32, width: 106.18 },
  o:       { left: 81.45,  top: 241.42, width: 63.99 },
  s:       { left: 122.33, top: 240.24, width: 68.29 },
  cowork:  { left: 171.79, top: 186.18, width: 402.46 },
} as const

function finalStyle(key: keyof typeof LAYOUT, zIndex: number) {
  const l = LAYOUT[key]
  return {
    position: 'absolute' as const,
    left: pct(l.left, CANVAS_W),
    top: pct(l.top, CANVAS_H),
    width: pct(l.width, CANVAS_W),
    height: 'auto' as const,
    zIndex,
  }
}

// ── Icon stage ───────────────────────────────────────────────────────────────
// The 4 v2 layers already share one identical canvas/coordinate system with
// each other, so they are never positioned individually — this single box places
// that shared canvas within the page, sized/centered to sit above the (locked)
// wordmark. Every layer inside is stacked at inset:0, 100%/100%, no exceptions.
// Scaled up ~17% from the previous size (same centering/gap anchor to the
// wordmark, just a larger target glyph height) — composition unchanged.
const ICON_BOX = { left: -0.666, top: -22.2, width: 99.42, height: 129.86 } // % of stage

// Start-time offsets (ms) — tightened stagger + longer overlapping durations so the
// 4 pieces read as one coordinated motion instead of 4 sequential ones, and the
// hand-off into the wordmark has no noticeable gap. Everything scaled ~1.25× slower
// than the previous pass (same relative rhythm, just more time to follow). Total assembly ≈ 3.4s.
const T = {
  purple: 0,
  green: 113,
  orange: 225,
  overlap: 475,
  // typography sped back up — icon timing above is untouched
  j: 1280,
  o: 1390,
  s: 1500,
  cowork: 1630,
}

const SYMBOL_DONE = 1250    // ms — symbol fully assembled, typography phase begins
const TYPE_DONE = 2180      // ms — typography fully assembled
const HOLD_MS = 560
const OUT_DURATION = 600

export default function SplashScreen({ onDone }: { onDone: () => void }) {
  const [phase, setPhase] = useState<Phase>('symbol')
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    setReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [])

  useEffect(() => {
    if (reducedMotion) {
      const t = [
        setTimeout(() => setPhase('hold'), 50),
        setTimeout(() => setPhase('out'), 50 + 300),
        setTimeout(onDone, 50 + 300 + 300),
      ]
      return () => t.forEach(clearTimeout)
    }
    const holdEnd = TYPE_DONE + HOLD_MS
    const t = [
      setTimeout(() => setPhase('typography'), SYMBOL_DONE),
      setTimeout(() => setPhase('hold'), TYPE_DONE),
      setTimeout(() => setPhase('out'), holdEnd),
      setTimeout(onDone, holdEnd + OUT_DURATION),
    ]
    return () => t.forEach(clearTimeout)
  }, [onDone, reducedMotion])

  const progress = phase === 'symbol' ? 45 : phase === 'typography' ? 90 : 100
  const ms = (v: number) => v / 1000
  const noTransform = { x: 0, y: 0, rotate: 0, scale: 1, opacity: 1 }

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#FFFFFF', pointerEvents: phase === 'out' ? 'none' : 'auto' }}
      animate={phase === 'out' ? { opacity: 0, scale: 1.03 } : { opacity: 1, scale: 1 }}
      transition={{ duration: ms(OUT_DURATION), ease: 'easeInOut' as const }}
    >
      {/* ─── Subtle background glow ───────────────────────────── */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 70%)' }}
        />
      </div>

      {/* ─── logo-stage — ONE relative container. Every one of the 8 assets is
           absolutely positioned inside it at its exact final left/top/width.
           Nothing here uses flex/gap — the layout IS the reconstructed logo. ── */}
      <motion.div
        className="logo-stage relative"
        style={{ width: 'clamp(260px, 40vw, 380px)', aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}
        animate={{ scale: phase === 'hold' ? [0.97, 1.015, 1] : 1 }}
        transition={{ duration: 0.42, ease: EASE, times: [0, 0.55, 1] }}
      >
        {/* ── Icon stage — the 4 v2 layers share one identical canvas, so every
             layer is stacked at inset:0 / 100%×100%, never positioned individually.
             This wrapper is the ONLY thing that places the icon within the page. ── */}
        <div
          className="absolute"
          style={{
            left: `${ICON_BOX.left}%`,
            top: `${ICON_BOX.top}%`,
            width: `${ICON_BOX.width}%`,
            height: `${ICON_BOX.height}%`,
          }}
        >
          {reducedMotion ? (
            <>
              <img src={logoPurple} alt="" className="absolute inset-0 h-full w-full" style={{ zIndex: 1 }} />
              <img src={logoGreen} alt="" className="absolute inset-0 h-full w-full" style={{ zIndex: 2 }} />
              <img src={logoOrange} alt="" className="absolute inset-0 h-full w-full" style={{ zIndex: 3 }} />
              <img src={logoOverlap} alt="" className="absolute inset-0 h-full w-full" style={{ zIndex: 4 }} />
            </>
          ) : (
            <>
              {/* ── Icon pieces: separated, then converge into this exact shared position ── */}
              <motion.img
                src={logoPurple}
                alt=""
                className="absolute inset-0 h-full w-full"
                style={{ zIndex: 1 }}
                initial={{ opacity: 0, x: -190, y: -145 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: ms(T.purple), duration: 1.2, ease: EASE }}
              />
              <motion.img
                src={logoGreen}
                alt=""
                className="absolute inset-0 h-full w-full"
                style={{ zIndex: 2 }}
                initial={{ opacity: 0, x: 190, y: -145 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: ms(T.green), duration: 1.2, ease: EASE }}
              />
              <motion.img
                src={logoOrange}
                alt=""
                className="absolute inset-0 h-full w-full"
                style={{ zIndex: 3 }}
                initial={{ opacity: 0, x: 0, y: 195 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: ms(T.orange), duration: 1.13, ease: EASE }}
              />
              <motion.img
                src={logoOverlap}
                alt=""
                className="absolute inset-0 h-full w-full"
                style={{ zIndex: 4 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: ms(T.overlap), duration: 0.75, ease: EASE }}
              />
            </>
          )}
        </div>

        {reducedMotion ? (
          <>
            <img src={letterJ} alt="J" style={finalStyle('j', 5)} />
            <img src={letterO} alt="o" style={finalStyle('o', 5)} />
            <img src={letterS} alt="s" style={finalStyle('s', 5)} />
            <img src={wordCowork} alt="CoWork" style={finalStyle('cowork', 5)} />
          </>
        ) : (
          <>
            {/* ── Typography: 1.0–1.7s in, locked by ~2.0s — UNCHANGED ── */}
            <motion.img
              src={letterJ}
              alt="J"
              style={finalStyle('j', 5)}
              initial={{ opacity: 0, x: -90, scale: 0.85 }}
              animate={phase !== 'symbol' ? noTransform : undefined}
              transition={{ delay: ms(T.j - SYMBOL_DONE), duration: 0.45, ease: EASE }}
            />
            <motion.img
              src={letterO}
              alt="o"
              style={finalStyle('o', 5)}
              initial={{ opacity: 0, x: -50, y: 40, scale: 0.85 }}
              animate={phase !== 'symbol' ? noTransform : undefined}
              transition={{ delay: ms(T.o - SYMBOL_DONE), duration: 0.45, ease: EASE }}
            />
            <motion.img
              src={letterS}
              alt="s"
              style={finalStyle('s', 5)}
              initial={{ opacity: 0, x: -50, y: -40, scale: 0.85 }}
              animate={phase !== 'symbol' ? noTransform : undefined}
              transition={{ delay: ms(T.s - SYMBOL_DONE), duration: 0.45, ease: EASE }}
            />
            <motion.img
              src={wordCowork}
              alt="CoWork"
              style={finalStyle('cowork', 5)}
              initial={{ opacity: 0, x: 130, scale: 0.85 }}
              animate={phase !== 'symbol' ? noTransform : undefined}
              transition={{ delay: ms(T.cowork - SYMBOL_DONE), duration: 0.55, ease: EASE }}
            />
          </>
        )}
      </motion.div>

      {/* ─── Progress indicator, synced with assembly phase ──────── */}
      <div
        className="absolute bottom-14 left-1/2 -translate-x-1/2 overflow-hidden rounded-full"
        style={{ width: 180, height: 2, background: 'rgba(99,102,241,0.12)' }}
      >
        <motion.div
          className="h-full rounded-full"
          style={{ background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)' }}
          initial={{ width: '0%' }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.45, ease: 'easeOut' as const }}
        />
      </div>
    </motion.div>
  )
}
